// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { pinnedStrings } from './strings';
import type { PinnedHeaderBadge } from './types';

export interface PinnedMessagesHeaderProps {
  readonly badge: PinnedHeaderBadge | null;
  readonly onClose?: (() => void) | undefined;
}

/**
 * Header bar for the pinned-messages screen. Shows the count badge with the
 * four discrete renderings (`1`, `2`, `3`, `3+`) and the optional close
 * affordance on web/desktop modal hosts.
 */
export const PinnedMessagesHeader = React.memo<PinnedMessagesHeaderProps>(({ badge, onClose }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface.primary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityRole="header"
    >
      <View style={styles.titleRow}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {pinnedStrings.screenTitle}
        </Text>
        {badge !== null && (
          <View
            style={[styles.badge, { backgroundColor: theme.colors.accent.indigo }]}
            accessibilityRole="text"
            accessibilityLabel={badgeAccessibilityLabel(badge)}
          >
            <Text style={styles.badgeText}>{badge}</Text>
          </View>
        )}
      </View>
      {onClose !== undefined && (
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close pinned messages"
          style={({ pressed }) => [styles.closeButton, pressed && { opacity: 0.6 }]}
        >
          <Text style={[styles.closeText, { color: theme.colors.text.secondary }]}>×</Text>
        </Pressable>
      )}
    </View>
  );
});

PinnedMessagesHeader.displayName = 'PinnedMessagesHeader';

function badgeAccessibilityLabel(badge: PinnedHeaderBadge): string {
  if (badge === '3+') {
    return pinnedStrings.countOverflow;
  }
  if (badge === '1') {
    return pinnedStrings.countSingular;
  }
  return pinnedStrings.countPlural(Number.parseInt(badge, 10));
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '600',
    marginRight: 8,
  } satisfies TextStyle,
  badge: {
    minWidth: 24,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  closeText: {
    fontSize: 22,
    lineHeight: 22,
  } satisfies TextStyle,
});
